/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import { Trans } from '@lingui/react'
import classNames from 'classnames'
import deepmerge from 'deepmerge'
import { bool, shape } from 'prop-types'
import { parseUrl, stringifyUrl } from 'query-string'
import { PureComponent } from 'react'
import { compose } from 'redux'

import './SearchProductListItem.scss'
import { ABO_LABEL, PRODUCT_STATUS_AVAILABLE } from 'shared/consts'
import { AD_SERVER_METADATA_DATA_ATTR } from 'shared/consts/adServer'
import {
  GTM_EVENT_CLICK_PRODUCT,
  GTM_EVENT_CLICK_PRODUCT_IMAGE,
} from 'shared/consts/gtm'
import { OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import { PAGE_TYPE_SEARCH_RESULTS } from 'shared/consts/page'
import { PRICE_TYPES } from 'shared/consts/productTemplates'
import {
  isMarketplace,
  isVariantAttributes,
} from 'shared/experiments/utils/featureFlags'
import { isRebrandingActive } from 'shared/experiments/utils/rebranding'
import store from 'shared/store'
import { setOfferType, setProductUrl } from 'shared/store/ducks/events'
import { isMobile } from 'shared/utils/deviceClassUtils'
import { isCdnUrl } from 'shared/utils/imageUtils'
import { isEmpty } from 'shared/utils/objectUtils'
import {
  getPriceForType,
  getProductTemplate,
} from 'shared/utils/productTemplateUtils'
import { normalizeUrl } from 'shared/utils/url'
import CdnImage from 'views/components/atoms/CdnImage'
import DiscountBadge from 'views/components/atoms/DiscountBadge'
import IconArrowRight from 'views/components/atoms/Icon/IconArrowRight'
import Image from 'views/components/atoms/Image'
import Price from 'views/components/atoms/Price'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import LinkWithIcon from 'views/components/molecules/LinkWithIcon'
import ModalShowButton from 'views/components/molecules/ModalShowButton'
import StarRating from 'views/components/molecules/StarRating'
import TextWithIcon from 'views/components/molecules/TextWithIcon'
import {
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
  IMAGE_MIN_WIDTH,
  IMAGE_SIZES,
} from 'views/components/organisms/FilteredProductList/consts'
import ProductStatus from 'views/components/organisms/ProductStatus'
import SellerBox from 'views/components/organisms/SellerBox'
import TransWithHtml from 'views/components/utils/TransWithHtml'
import { promotionTypeMapping } from 'views/consts/promotions'
import withMountPoints from 'views/enhancers/withMountPoints'
import { withSearchStateAndUrlContext } from 'views/enhancers/withSearchStateAndUrlContext'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { getDiscountToShow } from 'views/utils/discount'
import {
  isHideAboSubscription,
  isRedPointsActive,
} from 'views/utils/featureToggleUtils'
import {
  isDiscountValid,
  isListPriceGreaterThanOrEqualToRetailPrice,
  isPricePresent,
  isProductAvailable,
  isProductOutOfStock,
} from 'views/utils/productUtils'
import { mountPointsType, ProductType } from 'views/utils/propTypes'
import { metadata } from 'views/utils/propTypes/adserver'

import CartForm from './CartForm'
import { CLASS_SELECTABLE } from './consts'
import {
  hasPersonalVariantAttributes,
  isSaeOffer,
  shouldRenderAboLabel,
  shouldRenderProductLabels,
} from './helpers'
import IdSwitcher from './IdSwitcher'
import ProductVariants from './ProductVariants'
import SearchProductLabels from './SearchProductLabels'

class SearchProductListItem extends PureComponent {
  state = this.getInitialState()

  isVariantsToggleEnabled() {
    const {
      publicRuntimeConfig: {
        pageProperties: { experiments = [] },
      },
    } = this.props

    return isVariantAttributes(experiments)
  }

  getNumberOfVariantAttributes() {
    return this.props.product.variant_attributes?.length || 0
  }

  shouldShowProductVariantsBox() {
    const shouldShowVariants =
      this.props.shouldShowVariantButtonsOnHits &&
      this.props.product.variants?.length > 1

    if (this.isVariantsToggleEnabled()) {
      return shouldShowVariants && this.getNumberOfVariantAttributes() < 2
    }

    return shouldShowVariants
  }

  isItemWithMultipleVariantAttributes() {
    return (
      this.isVariantsToggleEnabled() && this.getNumberOfVariantAttributes() > 1
    )
  }

  getVariantDiscount({ stockStatus, discount } = {}) {
    return isProductOutOfStock(stockStatus) ? null : discount
  }

  getPriceInfo(currentVariant) {
    const {
      product: {
        id,
        flags: { prescriptionMedicine },
        variants = [],
      },
    } = this.props

    const usedVariant = this.isItemWithMultipleVariantAttributes()
      ? this.getCheapestVariant(variants) || currentVariant
      : currentVariant

    return {
      productId: id,
      stockStatus: usedVariant.stockStatus,
      unitPricing: usedVariant.unitPricing,
      prices: usedVariant.prices,
      prescriptionMedicine,
    }
  }

  getCheapestVariant(variants) {
    const availableVariants = variants.filter(variant => {
      return (
        variant.stockStatus &&
        variant.stockStatus.status === PRODUCT_STATUS_AVAILABLE
      )
    })

    if (!availableVariants || availableVariants.length < 1) {
      return undefined
    }

    const sortedVariants = availableVariants.sort((a, b) => {
      const amountA = a.best_offer?.price?.amount
      const amountB = b.best_offer?.price?.amount

      if (!amountA && !amountB) {
        return 0
      }
      if (!amountA) {
        return 1
      }
      if (!amountB) {
        return -1
      }

      return amountA - amountB
    })
    return sortedVariants[0]
  }

  getInitialState() {
    const {
      product,
      product: {
        deeplink,
        descriptionShort,
        id,
        icd10,
        ods_partners,
        imageUrl,
        labels,
        manufacturer,
        multiplierRP,
        name,
        nowProductGroup,
        pharmaForm,
        promotion,
        promotionProduct,
        unit,
        unitQuantityString,
        // FIXME rename in mapper
        offers_count: offerCount = 0,
        seller_count: sellerCount = 0,
        best_offer: bestOffer,
        ean,
        pzn,
        upid,
        isbn,
        best_offer: {
          type,
          promotion: bestOfferPromotion,
          redpoints: { multiplier } = {},
        } = {},
        variants = [],
        objectID,
      },
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { pageType, experiments, featureToggles },
      },
    } = this.props

    const offerId = bestOffer?.id

    const sellerBoxParams = {
      sellerName: bestOffer?.seller?.name,
      sellerId: bestOffer?.seller?.id,
      offerType: bestOffer?.type,
      cssBoxClasses: {
        padding: 'u-padding-small',
        textAlign: 'u-text-align--left@medium',
        backgroundColor: 'u-background-color--greySky',
      },
      embedSellerModal: false,
    }

    const labelsWithoutAboLabel = labels.filter(
      labelName => labelName !== ABO_LABEL
    )

    const offerTypeSae = isSaeOffer({
      bestOffer,
      experiments,
      tenant,
    })

    const hideAboSubscription = isHideAboSubscription(featureToggles)

    const displayAboLabel =
      labels &&
      shouldRenderAboLabel({ offerTypeSae, labels }) &&
      !hideAboSubscription

    const currentVariant =
      variants.find(variant => variant.id === id) || product
    return {
      currentVariantId: id,
      discount: this.getVariantDiscount(currentVariant),
      packageDetails: {
        unit,
        unitQuantityString,
        pharmaForm,
        id,
        manufacturer,
      },
      priceInfo: this.getPriceInfo(currentVariant),
      promotion: promotion || promotionTypeMapping[bestOfferPromotion?.type],
      promotionProduct,
      canonicalURL: normalizeUrl(deeplink),
      isSearchPage: pageType === PAGE_TYPE_SEARCH_RESULTS,
      offers: {
        count: offerCount,
        currentOfferId: offerId,
      },
      offerTypeSae,
      descriptionShort,
      displayAboLabel,
      imageUrl,
      labelsWithoutAboLabel,
      multiplierRP: multiplierRP || multiplier,
      name,
      ods_partners,
      nowProductGroup,
      ean,
      pzn,
      upid,
      icd10,
      isbn,
      type,
      sellerCount,
      bestOffer,
      sellerBoxParams,
      experiments,
      featureToggles,
      objectID,
    }
  }

  extractVariantProps = ({
    canonicalURL,
    descriptionShort,
    discount,
    id,
    imageUrl,
    labels,
    multiplierRP,
    nowProductGroup,
    pharmaForm,
    prices,
    productName,
    promotion,
    promotionProduct,
    stockStatus,
    unit,
    pzn,
    isbn,
    ean,
    unitPricing,
    unitQuantityString,
    // FIXME rename in mapper
    offers_count: offerCount = 0,
    seller_count: sellerCount = 0,
    best_offer: {
      id: offerId,
      type,
      promotion: bestOfferPromotion,
      redpoints: { multiplier } = {},
    } = {},
    best_offer: bestOffer,
    sellerBoxParams = {
      sellerName: bestOffer?.seller?.name,
      sellerId: bestOffer?.seller?.id,
      offerType: bestOffer?.type,
      cssBoxClasses: {
        padding: 'u-padding-small',
        textAlign: 'u-text-align--left@medium',
        backgroundColor: 'u-background-color--greySky',
      },
      embedSellerModal: false,
    },
    objectID,
  }) => ({
    currentVariantId: id,
    discount: isProductOutOfStock(stockStatus) ? null : discount,
    packageDetails: {
      pharmaForm,
      unit,
      unitQuantityString,
      id,
    },
    priceInfo: { productId: id, stockStatus, prices, unitPricing },
    name: productName,
    displayAboLabel: Boolean(labels?.includes(ABO_LABEL)),
    labelsWithoutAboLabel:
      labels?.filter(labelName => labelName !== ABO_LABEL) || [],
    offers: {
      count: offerCount,
      currentOfferId: offerId,
    },
    promotion: promotion || promotionTypeMapping[bestOfferPromotion?.type],
    promotionProduct,
    canonicalURL,
    descriptionShort,
    imageUrl,
    multiplierRP: multiplierRP || multiplier,
    nowProductGroup,
    type,
    pzn,
    isbn,
    ean,
    sellerCount,
    sellerBoxParams,
    objectID,
  })

  handleVariantUpdate = variant => {
    const {
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { experiments },
      },
    } = this.props
    const newState = this.extractVariantProps(variant)
    const { labelsWithoutAboLabel, displayAboLabel, multiplierRP } = newState

    const offerTypeSae = isSaeOffer({
      bestOffer: variant?.best_offer,
      experiments,
      tenant,
    })

    // Merge the props except of prices:
    // priceInfo contains also stock status
    this.setState(prevState => ({
      ...deepmerge(prevState, newState),
      displayAboLabel,
      labelsWithoutAboLabel,
      multiplierRP,
      offerTypeSae,
      priceInfo: {
        ...prevState.priceInfo,
        prices: newState.priceInfo.prices,
        stockStatus: newState.priceInfo.stockStatus,
        unitPricing: newState.priceInfo.unitPricing,
      },
      offers: {
        count: newState.offers.count,
        currentOfferId: newState.offers.currentOfferId,
      },
    }))
  }

  hasAddToCartButton = (stockStatus, { count = 0, currentOfferId }) => {
    const {
      product: {
        flags: { prescriptionMedicine },
      },
      publicRuntimeConfig: {
        publicConfig: { tenant },
        pageProperties: { experiments },
      },
    } = this.props

    const hasMarketplaceEnabledWithOffersAvailable =
      isMarketplace(experiments, tenant) && !(count === 1 && currentOfferId)

    if (
      hasMarketplaceEnabledWithOffersAvailable ||
      this.isItemWithMultipleVariantAttributes()
    ) {
      return false
    }

    return !prescriptionMedicine && !isProductOutOfStock(stockStatus)
  }

  getProductTemplate() {
    const {
      product,
      publicRuntimeConfig: {
        publicConfig: { tenant },
      },
    } = this.props

    return getProductTemplate({ ...product, tenant })
  }

  /*
   * props redPoints, redPointsMultiplierIsValid, redPointsIsActive aren't provided from algolia
   * if the multiplier is provided and is greater than 1 => always show the label
   * we need to set the rest of the props manually to pass the shouldRenderLabel check
   */
  getDummyRedpointsData(multiplierRP) {
    const productTemplate = this.getProductTemplate()

    return {
      multiplierRP,
      redPoints: 1,
      redPointsMultiplierIsValid: true,
      redPointsIsActive: true,
      productTemplate,
    }
  }

  getProductUrl = eventName => {
    const {
      queryParams: searchQueryParams,
      product: { variant_attributes },
      metadata,
    } = this.props
    const { isSearchPage, objectID } = this.state
    let { canonicalURL } = this.state
    const metadataContent = metadata[objectID] || {}
    const { trackingUrls = {} } = metadataContent
    const clickUrl = trackingUrls?.clickUrl

    if (clickUrl) {
      canonicalURL = clickUrl
    }

    if (isSearchPage) {
      const personalVariantAttributesQueryParameter =
        this.isItemWithMultipleVariantAttributes() &&
        hasPersonalVariantAttributes(variant_attributes)
          ? { master: 'true' }
          : {}

      const newQueryParams = {
        eventName,
        ...searchQueryParams,
        ...personalVariantAttributesQueryParameter,
      }

      const parsedProductUrl = parseUrl(canonicalURL, { decode: false })
      const query = {
        ...newQueryParams,
        ...parsedProductUrl.query,
      }

      const { url } = parsedProductUrl

      return stringifyUrl(
        {
          url,
          query,
        },
        { encode: false }
      )
    }

    return `${canonicalURL}`
  }

  getOffersUrl = () => {
    const { mountPoints } = this.props
    const { isSearchPage, currentVariantId } = this.state

    const url = mountPoints.getOffersUrl(currentVariantId)

    if (isSearchPage) {
      return `${url}?eventType=view&eventName=viewOffers_search`
    }

    return url
  }

  handleProductAboLabelClick = () => {
    store.dispatch(setProductUrl({ productUrl: this.state.canonicalURL }))
    store.dispatch(setOfferType({ offerType: this.state.type }))
  }

  handleProductCardClick = event => {
    if (event.currentTarget != event.target) {
      return
    }
    const {
      publicRuntimeConfig: {
        publicConfig: { deviceClass },
      },
    } = this.props
    const { target } = event
    const isElemSellerBox = target.getAttribute('data-target') === 'seller-box'
    const isSelectable =
      !isMobile(deviceClass) &&
      (target.closest(`.${CLASS_SELECTABLE}`) || isElemSellerBox)
    const shouldRedirect =
      target.tagName.toLowerCase() !== 'a' &&
      !target.closest('a') &&
      !isElemSellerBox

    if (isSelectable) {
      event.preventDefault()
    } else if (shouldRedirect) {
      window.location.href = this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)
    }
  }

  getRenderConditionals({ listPrice, retailPrice }) {
    const {
      product,
      product: {
        flags: { prescriptionMedicine },
        ratingCount,
      },
      publicRuntimeConfig: {
        publicConfig: { discountBoundaries, tenant },
        pageProperties: { experiments },
      },
    } = this.props

    const {
      discount,
      nowProductGroup,
      offers,
      offers: { count: offersCount },
      priceInfo: { stockStatus = product.stockStatus },
      type,
      featureToggles,
    } = this.state

    const hasOffers = offersCount > 1
    let shouldDisplayNowWidget = nowProductGroup === 'sameday_nextday'
    if (shouldDisplayNowWidget && isMarketplace(experiments, tenant)) {
      shouldDisplayNowWidget = type === OFFER_TYPE_SAE
    }

    return {
      hasAddToCartButton: this.hasAddToCartButton(stockStatus, offers),
      isAvailable: isProductAvailable(stockStatus),
      isListPricePresent:
        !prescriptionMedicine &&
        isListPriceGreaterThanOrEqualToRetailPrice({ listPrice, retailPrice }),
      isRetailPricePresent: isPricePresent(retailPrice),
      isValidDiscount: isDiscountValid(discount, discountBoundaries),
      shouldDisplayLinkToOfferPage:
        !this.isItemWithMultipleVariantAttributes() &&
        hasOffers &&
        isMarketplace(experiments, tenant),
      shouldDisplayMpOrMultiVariantsLayout:
        this.isItemWithMultipleVariantAttributes() ||
        (isMarketplace(experiments, tenant) && hasOffers),
      shouldDisplayNowWidget,
      shouldDisplayRatings: !prescriptionMedicine && ratingCount > 0,
      shouldShowSellerBox: offersCount === 1,
      isRedPointsFeatureToggleActive: isRedPointsActive(featureToggles),
    }
  }

  render() {
    const {
      hideStockStatusOnMobile,
      isFirst,
      product,
      product: {
        flags: { prescriptionMedicine },
        prescriptionType,
        rating,
        ratingCount,
        sponsored,
        sponsoredProductLabel,
        variants,
      },
      lazyLoadImage,
      publicRuntimeConfig: {
        publicConfig,
        publicConfig: { superScripts, tenant, onlineDoctorConfig },
        pageProperties: {
          isSubscriptionProduct,
          experiments: featureExperiments,
        },
      },
      queryParams,
      showDiscountLabel,
      metadata,
    } = this.props

    const {
      currentVariantId,
      descriptionShort,
      discount,
      displayAboLabel,
      ean,
      pzn,
      icd10,
      isbn,
      imageUrl,
      labelsWithoutAboLabel,
      offerTypeSae,
      bestOffer,
      offers: { currentOfferId },
      packageDetails: {
        id,
        manufacturer,
        pharmaForm,
        unit,
        unitQuantityString,
      },
      priceInfo: { prices, stockStatus = product.stockStatus, unitPricing },
      promotion,
      promotionProduct,
      ods_partners,
      multiplierRP,
      name,
      sellerCount,
      upid,
      sellerBoxParams,
      experiments,
      objectID,
    } = this.state
    const productTemplate = this.getProductTemplate()
    const listPrice = getPriceForType(prices, PRICE_TYPES.LIST)
    const retailPrice = getPriceForType(prices, PRICE_TYPES.RETAIL)
    const supNoteNumber = superScripts?.supNoteNumber
    const discountsToShow = getDiscountToShow({ discount, tenant })

    const metadataContent = metadata[objectID] || {}
    const { trackingUrls = {} } = metadataContent

    const {
      hasAddToCartButton,
      isAvailable,
      isListPricePresent,
      isRetailPricePresent,
      isValidDiscount,
      shouldDisplayLinkToOfferPage,
      shouldDisplayNowWidget,
      shouldDisplayMpOrMultiVariantsLayout,
      shouldDisplayRatings,
      shouldShowSellerBox,
      isRedPointsFeatureToggleActive,
    } = this.getRenderConditionals({ listPrice, retailPrice })

    let redPointsData
    if (isRedPointsFeatureToggleActive && multiplierRP) {
      redPointsData = this.getDummyRedpointsData(multiplierRP)
    }
    const discountLabel = (
      <>
        <span>&minus;{discount}%</span>
        {supNoteNumber && <sup>{supNoteNumber}</sup>}
      </>
    )

    const labelsProps = {
      displayAboLabel,
      displayNowWidget: shouldDisplayNowWidget,
      offerTypeSae,
      otherLabels: labelsWithoutAboLabel,
      prescription: prescriptionMedicine,
      promotion,
      promotionProduct,
      redPointsData,
      showDiscountLabel: showDiscountLabel && isValidDiscount,
      sponsored,
      upid,
      trackingUrls,
    }

    const isRebrandingEnabled = isRebrandingActive(featureExperiments)

    const retailPriceClasses = classNames(
      'a-h2 u-no-margin o-SearchProductListItem__prices__retail-price',
      {
        'u-color--red': isValidDiscount,
      }
    )

    const itemClassName = classNames(
      'o-SearchProductListItem u-padding--top u-background-color--background u-position--relative u-border u-border-radius',
      {
        'o-SearchProductListItem--first':
          isFirst && shouldRenderProductLabels(labelsProps),
      }
    )

    const textInfoClassName = classNames(
      'u-padding--left l-flex l-flex--vertical-stretch u-border--left@medium l-col--12-of-12 l-col--5-of-12@medium o-SearchProductListItem__content__body',
      {
        'l-flex--distribute-space': descriptionShort,
        'l-flex--justify-content--end': !descriptionShort,
      }
    )

    const stockStatusClassName = classNames(
      'a-h4-tiny u-margin-small--bottom@small-tiny',
      {
        'u-hidden@small-tiny': hideStockStatusOnMobile && isAvailable,
      }
    )

    const titleClassName = classNames(
      'o-SearchProductListItem__title u-font-weight--bold u-no-margin a-h3@medium',
      {
        'u-margin-xx-small--bottom': shouldDisplayRatings,
      }
    )

    const renderProductItemLinkWithIcon = () => {
      if (this.isItemWithMultipleVariantAttributes()) {
        return (
          <div className="o-PointerOnHover">
            <LinkWithIcon
              href={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)}
              icon={<IconArrowRight className="u-color--blue" />}
              right
            >
              <TransWithHtml
                id="product.variants.configure.link"
                className="u-font-weight--bold u-color--blue u-no-wrap o-PointerOnHover__text"
                element="span"
              />
            </LinkWithIcon>
          </div>
        )
      }
      if (shouldDisplayLinkToOfferPage) {
        return (
          <div className="o-PointerOnHover">
            <LinkWithIcon
              href={this.getOffersUrl()}
              icon={<IconArrowRight className="u-color--blue" />}
              right
            >
              <TransWithHtml
                id="product.seller.count"
                values={{ count: sellerCount }}
                className="u-font-weight--bold u-color--blue u-no-wrap o-PointerOnHover__text"
                element="span"
              />
            </LinkWithIcon>
          </div>
        )
      }
    }

    const renderProductVariantsBox = () =>
      this.shouldShowProductVariantsBox() ? (
        <ProductVariants
          currentVariantId={currentVariantId}
          variants={variants}
          updateCurrentVariant={this.handleVariantUpdate}
        />
      ) : null

    const searchProductListItemProps = {
      className: itemClassName,
      'data-qa-id': 'result-list-entry',
      'data-issubscriptionproduct': isSubscriptionProduct,
      'data-clientside-hook': 'FilteredProductListItem',
      [AD_SERVER_METADATA_DATA_ATTR]: !isEmpty(metadata[objectID])
        ? JSON.stringify(metadata[objectID] || {})
        : undefined,
    }

    return (
      <li {...searchProductListItemProps}>
        <SearchProductLabels
          {...labelsProps}
          handleProductAboLabelClick={this.handleProductAboLabelClick}
          discountLabel={discountLabel}
          sponsoredProductLabel={sponsoredProductLabel}
        />
        <div
          className="o-SearchProductListItem__link u-color--dark u-padding--sides u-padding--bottom l-flex l-flex--wrap l-flex--stretch-children"
          data-qa-id={
            Boolean(currentOfferId) && `offers-link-${currentOfferId}`
          }
          data-clientside-hook="productDetailButton"
          onClick={event => {
            this.handleProductCardClick(event)
          }}
        >
          <a
            className="o-SearchProductListItem__image"
            href={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT_IMAGE)}
            draggable={false}
          >
            {isCdnUrl(publicConfig, imageUrl) ? (
              <CdnImage
                fileName={imageUrl}
                alt={name}
                className="a-fullwidth-image"
                sizes={IMAGE_SIZES}
                minWidth={IMAGE_MIN_WIDTH}
                maxWidth={IMAGE_MAX_WIDTH}
                maxHeight={IMAGE_MAX_HEIGHT}
                lazyLoad={lazyLoadImage}
                preconnect={!lazyLoadImage}
              />
            ) : (
              <Image
                src={imageUrl}
                alt={name}
                className="a-fullwidth-image"
                lazyLoad={lazyLoadImage}
                preconnect={!lazyLoadImage}
              />
            )}
          </a>
          <div className="o-SearchProductListItem__content l-flex l-flex--stretch-children l-flex__primary l-flex--wrap@small-tiny">
            <div className="l-col--12-of-12 l-col--7-of-12@medium u-padding--sides o-SearchProductListItem__content__header">
              <div className="u-margin--bottom o-SearchProductListItem__content__header__top">
                <a
                  className={titleClassName}
                  href={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)}
                  draggable={false}
                >
                  {name}
                </a>
                {shouldDisplayRatings && (
                  <StarRating
                    {...{
                      rating,
                      ratingCount,
                      ratingCountColor: 'dark',
                      href: this.getProductUrl(GTM_EVENT_CLICK_PRODUCT),
                    }}
                  />
                )}
              </div>
              <div className={CLASS_SELECTABLE}>
                <ul className="o-SearchProductListItem__info-list u-margin--bottom u-margin-small--bottom@small-tiny">
                  {!this.isItemWithMultipleVariantAttributes() && (
                    <li className="o-SearchProductListItem__info-list__item">
                      <span
                        className="a-h4-tiny u-font-weight--bold o-SearchProductListItem__info__items"
                        suppressHydrationWarning
                      >
                        {unitQuantityString} {unit}
                        {pharmaForm ? ` | ${pharmaForm}` : ''}
                      </span>
                    </li>
                  )}
                  {!this.isItemWithMultipleVariantAttributes() && (
                    <li className="u-color--grey u-hidden@small-tiny o-SearchProductListItem__info-list__item">
                      <span
                        className="a-h4-tiny o-SearchProductListItem__info__items"
                        suppressHydrationWarning
                      >
                        <IdSwitcher
                          pzn={pzn}
                          ean={ean}
                          upid={upid}
                          uid={id}
                          isbn={isbn}
                        />
                      </span>
                    </li>
                  )}

                  {manufacturer && (
                    <li className="u-color--grey o-SearchProductListItem__info-list__item">
                      <span className="a-h4-tiny o-SearchProductListItem__info__items">
                        {manufacturer}
                      </span>
                    </li>
                  )}
                </ul>
                <a
                  href={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)}
                  draggable={false}
                  className="a-link--no-underline"
                >
                  <ProductStatus
                    stockStatus={stockStatus}
                    dot
                    className={stockStatusClassName}
                  />
                </a>
              </div>
            </div>
            {isRebrandingEnabled && renderProductItemLinkWithIcon()}
            <div className={textInfoClassName}>
              {descriptionShort && (
                <a
                  href={this.getProductUrl(GTM_EVENT_CLICK_PRODUCT)}
                  draggable={false}
                  className="a-link--neutral u-hidden@small-tiny"
                >
                  <p className="a-h4-tiny o-SearchProductListItem__content__body__text">
                    {descriptionShort}
                  </p>
                </a>
              )}

              {ods_partners &&
                ods_partners.length > 0 &&
                onlineDoctorConfig?.enabled && (
                  <div
                    id="odsModalButtonSERP"
                    name={`odsModalProduct-${JSON.stringify(icd10)}`}
                    data-clientside-hook="ods-modal-trigger-buttonSERP"
                    className="odsModalbuttonDiv u-color--blue u-padding-xx-large--bottom u-font-weight--bold"
                  >
                    <ModalShowButton
                      component={ButtonAsLink}
                      id="odsModalButton"
                      name="buybox-large_online-doctor-service"
                      modalId={`modalIdOdsSERP`}
                    >
                      <TextWithIcon
                        id="odsModalButton"
                        qaId="show-ods-modal-button-serp"
                        className="u-color--blue"
                        icon={<IconArrowRight className="u-color--blue" />}
                        right
                      >
                        <Trans id="buyBox.link.label.onlineDoctorService.zava" />
                      </TextWithIcon>
                    </ModalShowButton>
                  </div>
                )}

              <div className="l-flex l-flex--distribute-space l-flex--end l-flex--wrap o-SearchProductListItem__content__buy-section">
                <div className="u-margin--right o-SearchProductListItem__content__buy-section-wrapper">
                  {!isRebrandingEnabled && renderProductItemLinkWithIcon()}
                  <div className="l-flex o-SearchProductListItem__prices__retail-price-wrapper">
                    {!showDiscountLabel && discountsToShow ? (
                      <DiscountBadge
                        discount={discount}
                        ellipse
                        rounded
                        smaller
                        className="u-margin-small--right u-font-weight--bold"
                      >
                        <span suppressHydrationWarning className="u-no-wrap">
                          &minus;{discount}%
                        </span>
                      </DiscountBadge>
                    ) : null}
                    {isRetailPricePresent && (
                      <p
                        className={retailPriceClasses}
                        data-qa-id="entry-price"
                      >
                        {shouldDisplayMpOrMultiVariantsLayout && (
                          <>
                            <Trans id="general.label.price.from" />
                            &nbsp;
                          </>
                        )}
                        <Price {...retailPrice} />
                      </p>
                    )}
                  </div>
                  {unitPricing && (
                    <p className="u-no-margin u-margin-xx-small--bottom u-no-wrap u-color--grey o-SearchProductListItem__prices__unitPricing">
                      {unitPricing}
                    </p>
                  )}
                  {isListPricePresent && isValidDiscount && (
                    <p className="l-flex u-no-margin o-SearchProductListItem__prices__list-price-wrapper">
                      <div className="o-SearchProductListItem__prices__list-price__text">
                        {offerTypeSae ? (
                          <Trans id={listPrice?.info?.text} />
                        ) : (
                          <Trans id="general.label.miraklListPrice" />
                        )}
                        <sup>{listPrice?.info?.index}</sup>
                      </div>
                      <span className="u-text-decoration--line-through u-margin-xx-small--left o-SearchProductListItem__prices__list-price">
                        {offerTypeSae ? (
                          <Price {...listPrice} />
                        ) : (
                          bestOffer?.strike_price?.formatted
                        )}
                      </span>
                    </p>
                  )}
                </div>
                <div className="l-flex@medium l-flex--justify-content--end o-SearchProductListItem__content__buy-section__cart-section">
                  {hasAddToCartButton && (
                    <CartForm
                      algoliaQueryId={queryParams.queryID}
                      offerId={currentOfferId}
                      position={queryParams.position}
                      prescriptionType={prescriptionType}
                      productId={currentVariantId}
                      productTemplate={productTemplate}
                      stockStatus={stockStatus}
                      trackingUrls={trackingUrls}
                    />
                  )}
                </div>
                {shouldShowSellerBox && isMarketplace(experiments, tenant) && (
                  <SellerBox {...sellerBoxParams} />
                )}
              </div>
            </div>
          </div>
        </div>
        {renderProductVariantsBox()}
      </li>
    )
  }
}

SearchProductListItem.propTypes = {
  hideStockStatusOnMobile: bool,
  isFirst: bool,
  mountPoints: mountPointsType.isRequired,
  product: shape(ProductType).isRequired,
  lazyLoadImage: bool.isRequired,
  publicRuntimeConfig: shape({}),
  queryParams: shape({}),
  showDiscountLabel: bool,
  shouldShowVariantButtonsOnHits: bool,
  metadata,
}

SearchProductListItem.defaultProps = {
  hideStockStatusOnMobile: false,
  isFirst: false,
  publicRuntimeConfig: {},
  queryParams: {},
  showDiscountLabel: false,
  shouldShowVariantButtonsOnHits: true,
  metadata: {},
}

export default compose(
  withSearchStateAndUrlContext,
  withShopAndRuntimeConfig,
  withMountPoints
)(SearchProductListItem)
